import { API_BASE_URL } from '@/constants/api'

export interface TransferHistoryFile {
  fileId: string
  fileName: string
  fileSize: number
  displayName: string
  fileOrder: number
  isDeleted?: boolean
}

export interface TransferHistoryItem {
  id: string
  shareKey: string
  shareName: string
  fileCount: number
  totalSize: number
  downloadCount: number
  maxDownloads: number
  passwordProtected: boolean
  burnAfterRead: boolean
  status: string
  expiresAt: string
  createdAt: string
  files: TransferHistoryFile[]
  downloadUrl: string
  isExpired: boolean
  isActive: boolean
  description?: string
  encrypted?: boolean
  allowDownload?: boolean
  allowClone?: boolean
  allowTransfer?: boolean
}

export interface TransferHistoryResponse {
  items: TransferHistoryItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface TransferStatistics {
  totalShares: number
  activeShares: number
  expiredShares: number
  totalDownloads: number
  totalSize: number
  totalFiles: number
}

export async function getTransferHistory(page: number = 1, pageSize: number = 20): Promise<TransferHistoryResponse> {
  const token = localStorage.getItem('accessToken')
  
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/history/transfers?page=${page}&pageSize=${pageSize}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '获取传输历史失败')
  }

  return response.json()
}

export async function getTransferStatistics(): Promise<TransferStatistics> {
  const token = localStorage.getItem('accessToken')
  
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/history/statistics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '获取统计信息失败')
  }

  return response.json()
}

export async function deleteTransfer(shareId: string): Promise<void> {
  const token = localStorage.getItem('accessToken')
  
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/history/transfers/${shareId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '删除失败')
  }
}

export async function revokeTransfer(shareId: string): Promise<void> {
  const token = localStorage.getItem('accessToken')
  
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(`${API_BASE_URL}/history/transfers/${shareId}/revoke`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '撤销失败')
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 30) return `${days}天前`
  
  return date.toLocaleDateString('zh-CN')
}

export function isShareExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date()
}
